import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Dayjs } from 'dayjs';
import { t } from 'i18next';

import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';

import { CycleSegment } from './CycleSegment';
import { SegmentTypes, useGetCycleSegments } from './hooks/useGetCycleSegments';
import { useGetIdleSegments } from './hooks/useGetIdleSegments';
import { useIndicatorValues } from './hooks/useIndicatorValues';
import { useNoSignalBlocks } from './hooks/useNoSignalBlocks';
import { PauseInterval } from './hooks/usePauseSegments';
import { useTimeNormalization } from './hooks/useTimeNormalization';
import { useWholeHours } from './hooks/useWholeHours';
import { HourMarker } from './HourMarker';
import { IdleSegment } from './IdleSegment';
import { LoadSegment } from './LoadSegment';
import { LoadSegmentNumber } from './LoadSegmentNumber';
import NoSignal from './NoSegment';
import { PauseSegment } from './PauseSegment';
import TimeIndicator from './TimeIndicator';

interface LoadTimelineSegment {
  id: string;
  start: Dayjs;
  end: Dayjs;
}

interface CycleTimelineProps {
  pings: LocationHistoryMinimal[];
  loadSegments: LoadTimelineSegment[];
  hoveredLoadId: string | null;
  pauseSegments: PauseInterval[];
}

export const CycleTimeline = ({
  pings,
  loadSegments,
  hoveredLoadId,
  pauseSegments,
}: CycleTimelineProps) => {
  const minTime = pings[0].occurredAt.toISOString();
  const maxTime = pings[pings.length - 1].occurredAt.toISOString();

  const { wholeHours, determineInterval } = useWholeHours(minTime, maxTime);
  const normalize = useTimeNormalization(minTime, maxTime);

  const segments = useGetCycleSegments(pings);
  const idleSegments = useGetIdleSegments(pings);

  const noSignalBlocks = useNoSignalBlocks(segments);

  const hasLogging = segments.some((segment) => segment.type === SegmentTypes.LOGGING);

  const { graphRef, indicatorValues } = useIndicatorValues(
    minTime,
    maxTime,
    noSignalBlocks,
  );

  const interval = determineInterval();

  const filteredHours = wholeHours.filter((hour, index) => index % interval === 0);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateAreas: `
          "loadLabel loadNumbers"
          "pickUpLabel graph"
          "enRouteLabel graph"
          "dropOffLabel graph"
          "loggingLabel graph"
        `,
        gridTemplateRows: 'auto auto auto auto auto',
        gridTemplateColumns: 'auto 1fr',
        columnGap: 2,
      }}
    >
      <Typography variant="body2" sx={{ gridArea: 'loadLabel' }}>
        {t('live_map.timeline.load')}
      </Typography>
      <Typography variant="body2" sx={{ gridArea: 'dropOffLabel' }}>
        {t('live_map.timeline.dropoff')}
      </Typography>
      <Typography variant="body2" sx={{ gridArea: 'enRouteLabel' }}>
        {t('live_map.timeline.enroute')}
      </Typography>
      <Typography variant="body2" sx={{ gridArea: 'pickUpLabel' }}>
        {t('live_map.timeline.pickup')}
      </Typography>
      {hasLogging && (
        <Typography variant="body2" sx={{ gridArea: 'loggingLabel' }}>
          {t('live_map.timeline.logging')}
        </Typography>
      )}

      {loadSegments.map((load, index) => (
        <Box
          key={load.id}
          sx={{
            gridArea: 'loadNumbers',
            position: 'relative',
          }}
        >
          <LoadSegmentNumber
            startPosition={normalize(load.start.toISOString())}
            endPosition={normalize(load.end.toISOString(), true)}
            loadNumber={index + 1}
          />
        </Box>
      ))}

      <Box
        ref={graphRef}
        sx={{
          gridArea: 'graph',
          position: 'relative',
          backgroundColor: 'background.paper',
        }}
      >
        <TimeIndicator
          visible={indicatorValues.indicatorVisible}
          position={indicatorValues.indicatorPosition}
          time={indicatorValues.indicatorTime}
        />

        {filteredHours.map((hour) => (
          <HourMarker key={hour} position={normalize(hour)} time={hour} />
        ))}

        {/* No-signal gaps are a backdrop, so render them first. A single-ping
            cycle segment collapses to a thin tick that sits right at the edge of
            the following gap; drawing the gap on top would paint over it, hiding
            sparse pings the rest of this work set out to surface (TRE-15314). */}
        {noSignalBlocks.map(([start, end]) => (
          <NoSignal
            key={`nosignal_${start}`}
            startPosition={normalize(start.toISOString())}
            endPosition={normalize(end.toISOString(), true)}
          />
        ))}

        {segments.map((segment, index) => (
          <CycleSegment
            key={`segment_${index}`}
            startPosition={normalize(segment.start.toISOString())}
            endPosition={normalize(segment.end.toISOString(), true)}
            type={segment.type}
            hasLogging={hasLogging}
          />
        ))}

        {idleSegments.map((segment, index) => (
          <IdleSegment
            key={`idle_${index}`}
            startPosition={normalize(segment.start.toISOString())}
            endPosition={normalize(segment.end.toISOString(), true)}
            type={segment.type}
            hasLogging={hasLogging}
          />
        ))}

        {loadSegments.map((load) => (
          <LoadSegment
            key={load.id}
            startPosition={normalize(load.start.toISOString())}
            endPosition={normalize(load.end.toISOString(), true)}
            isHovered={hoveredLoadId === load.id}
          />
        ))}

        {pauseSegments.map((seg, index) => (
          <PauseSegment
            key={`pause_${index}`}
            startPosition={normalize(seg.start.toISOString())}
            endPosition={normalize(seg.end.toISOString(), true)}
            type={seg.type}
            hasLogging={hasLogging}
            isOpen={seg.isOpen}
          />
        ))}
      </Box>
    </Box>
  );
};
